import { db, storage } from "../firebase/config";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  increment
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";

const FOLDERS_COLLECTION = "marketing_folders";
const DOCUMENTS_COLLECTION = "marketing_documents";

/**
 * Real-time subscription to all marketing folders
 */
export function subscribeToMarketingFolders(callback) {
  return onSnapshot(collection(db, FOLDERS_COLLECTION), (snapshot) => {
    const list = [];
    snapshot.forEach((docSnap) => {
      list.push({ id: docSnap.id, ...docSnap.data() });
    });
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to marketing folders:", error);
    callback([], error);
  });
}

/**
 * Real-time subscription to all marketing documents
 */
export function subscribeToMarketingDocuments(callback) {
  return onSnapshot(collection(db, DOCUMENTS_COLLECTION), (snapshot) => {
    const list = [];
    snapshot.forEach((docSnap) => {
      list.push({ id: docSnap.id, ...docSnap.data() });
    });
    list.sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to marketing documents:", error);
    callback([], error);
  });
}

/**
 * Create a new folder
 */
export async function createMarketingFolder({ name, parentId = null, color = "#3B82F6", createdBy = "User" }) {
  if (!name || !name.trim()) throw new Error("Folder name is required.");
  
  const folderData = {
    name: name.trim(),
    parentId: parentId || null,
    color: color || "#3B82F6",
    createdAt: new Date().toISOString(),
    createdBy: createdBy || "User"
  };

  const docRef = await addDoc(collection(db, FOLDERS_COLLECTION), folderData);
  return { id: docRef.id, ...folderData };
}

/**
 * Update folder properties (name, color, parentId)
 */
export async function updateMarketingFolder(folderId, data) {
  const docRef = doc(db, FOLDERS_COLLECTION, folderId);
  await updateDoc(docRef, data);
}

/**
 * Delete a folder and handle recursive subfolders & documents cleanup
 */
export async function deleteMarketingFolder(folderId, allFolders = [], allDocs = []) {
  // Find all child folder IDs recursively
  const folderIdsToDelete = [folderId];
  const findChildren = (pId) => {
    const children = allFolders.filter(f => f.parentId === pId);
    for (const child of children) {
      folderIdsToDelete.push(child.id);
      findChildren(child.id);
    }
  };
  findChildren(folderId);

  // Delete all documents in these folders
  const docsToDelete = allDocs.filter(d => folderIdsToDelete.includes(d.folderId));
  for (const docItem of docsToDelete) {
    if (docItem.storagePath) {
      try {
        const fileRef = ref(storage, docItem.storagePath);
        await deleteObject(fileRef);
      } catch (err) {
        console.warn("Could not delete file from storage during folder purge:", err);
      }
    }
    await deleteDoc(doc(db, DOCUMENTS_COLLECTION, docItem.id));
  }

  // Delete the folders
  for (const fId of folderIdsToDelete) {
    await deleteDoc(doc(db, FOLDERS_COLLECTION, fId));
  }
}

/**
 * Upload a document to Firebase Storage and record metadata in Firestore
 */
export async function uploadMarketingDocument({
  file,
  title,
  folderId = null,
  tags = [],
  description = "",
  uploadedBy = "User"
}) {
  if (!file) throw new Error("No file provided for upload.");

  const timestamp = Date.now();
  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `marketing_docs/${timestamp}_${safeFileName}`;
  const fileRef = ref(storage, storagePath);

  // Upload to Firebase Storage
  await uploadBytes(fileRef, file);
  const downloadUrl = await getDownloadURL(fileRef);

  // Determine file extension and type category
  const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
  let fileType = "generic";
  if (["pdf"].includes(extension)) fileType = "pdf";
  else if (["doc", "docx", "txt", "rtf", "odt"].includes(extension)) fileType = "document";
  else if (["xls", "xlsx", "csv"].includes(extension)) fileType = "spreadsheet";
  else if (["ppt", "pptx", "key"].includes(extension)) fileType = "presentation";
  else if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension)) fileType = "image";
  else if (["mp4", "mov", "avi", "webm", "mkv"].includes(extension)) fileType = "video";
  else if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) fileType = "archive";

  const docData = {
    title: (title || file.name).trim(),
    originalFileName: file.name,
    fileSize: file.size || 0,
    fileType,
    extension,
    mimeType: file.type || "application/octet-stream",
    folderId: folderId || null,
    downloadUrl,
    storagePath,
    tags: Array.isArray(tags) ? tags : [],
    description: (description || "").trim(),
    uploadedAt: new Date().toISOString(),
    uploadedBy: uploadedBy || "User",
    downloadsCount: 0
  };

  const docRef = await addDoc(collection(db, DOCUMENTS_COLLECTION), docData);
  return { id: docRef.id, ...docData };
}

/**
 * Update document metadata (title, folderId, tags, description)
 */
export async function updateMarketingDocument(docId, data) {
  const docRef = doc(db, DOCUMENTS_COLLECTION, docId);
  await updateDoc(docRef, data);
}

/**
 * Delete a document from Storage and Firestore
 */
export async function deleteMarketingDocument(docId, storagePath) {
  if (storagePath) {
    try {
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
    } catch (err) {
      console.warn("Storage deletion error (proceeding with firestore cleanup):", err);
    }
  }
  await deleteDoc(doc(db, DOCUMENTS_COLLECTION, docId));
}

/**
 * Increment the downloads counter for a document
 */
export async function incrementDocumentDownload(docId) {
  try {
    const docRef = doc(db, DOCUMENTS_COLLECTION, docId);
    await updateDoc(docRef, {
      downloadsCount: increment(1)
    });
  } catch (err) {
    console.error("Error incrementing download counter:", err);
  }
}
